/** Board access: participants, explicit roles, denials, exceptions, invitations and links. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, queryKeys } from '@/shared/api';

import type {
  AccessRequest,
  AccessRule,
  AccessibleBoard,
  BoardAccess,
  BoardOwnership,
  Impact,
  Invitation,
  InviteLink,
  LinkPreview,
  MemberRole,
  Participant,
  RedeemResult,
  TransferImpact,
  TransferResult,
} from './types';
import type { RoleInput } from './roles';

const base = (boardId: string) => `/v1/boards/${boardId}`;

/** After any access change both the board and every list built from it are stale. */
function useInvalidateBoardAccess(boardId: string) {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ['collab'] });
    void client.invalidateQueries({ queryKey: queryKeys.boards.detail(boardId) });
    void client.invalidateQueries({ queryKey: queryKeys.boards.all });
  };
}

export interface AccessibleBoardsFilter {
  q?: string;
  relation?: 'owned' | 'shared';
  favorites?: boolean;
  workspaceId?: string;
  limit?: number;
}

/** Every board the viewer can open, once, whatever grant provides it. */
export function useMyBoards(filter: AccessibleBoardsFilter = {}) {
  return useQuery({
    queryKey: queryKeys.collab.myBoardsFiltered(filter),
    queryFn: ({ signal }) =>
      api.get<AccessibleBoard[]>('/v1/me/boards', {
        signal,
        query: {
          q: filter.q || undefined,
          relation: filter.relation,
          favorites: filter.favorites ? true : undefined,
          workspace_id: filter.workspaceId,
          limit: filter.limit,
        },
      }),
  });
}

export function useParticipants(boardId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.collab.participants(boardId),
    queryFn: ({ signal }) => api.get<Participant[]>(`${base(boardId)}/participants`, { signal }),
    enabled,
  });
}

export function useBoardAccess(boardId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.collab.access(boardId),
    queryFn: ({ signal }) => api.get<BoardAccess>(`${base(boardId)}/access`, { signal }),
    enabled,
  });
}

export function useRoleTemplates(boardId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.collab.roleTemplates(boardId),
    queryFn: ({ signal }) =>
      api.get<Record<string, string[]>>(`${base(boardId)}/role-templates`, { signal }),
    enabled,
  });
}

export function useSetBoardMemberRole(boardId: string) {
  const invalidate = useInvalidateBoardAccess(boardId);
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: MemberRole }) =>
      api.put<Participant>(`${base(boardId)}/members/${userId}`, { role }),
    onSuccess: invalidate,
  });
}

export function useRemoveBoardMember(boardId: string) {
  const invalidate = useInvalidateBoardAccess(boardId);
  return useMutation({
    mutationFn: (userId: string) => api.delete(`${base(boardId)}/members/${userId}`),
    onSuccess: invalidate,
  });
}

export function useSetBoardDenial(boardId: string) {
  const invalidate = useInvalidateBoardAccess(boardId);
  return useMutation({
    mutationFn: ({ userId, denied }: { userId: string; denied: boolean }) =>
      denied
        ? api.put(`${base(boardId)}/denials/${userId}`)
        : api.delete(`${base(boardId)}/denials/${userId}`),
    onSuccess: invalidate,
  });
}

export function useSetUserExceptions(boardId: string) {
  const invalidate = useInvalidateBoardAccess(boardId);
  return useMutation({
    mutationFn: ({
      userId,
      granted,
      revoked,
    }: {
      userId: string;
      granted: string[];
      revoked: string[];
    }) => api.put(`${base(boardId)}/members/${userId}/exceptions`, { granted, revoked }),
    onSuccess: invalidate,
  });
}

export function useSetBoardRolePermissions(boardId: string) {
  const invalidate = useInvalidateBoardAccess(boardId);
  return useMutation({
    mutationFn: ({ role, permissions }: { role: MemberRole; permissions: string[] }) =>
      api.put(`${base(boardId)}/role-permissions/${role}`, { permissions }),
    onSuccess: invalidate,
  });
}

// --- ownership --------------------------------------------------------------------------------

/** Owner, immutable creator, origin team and (for administrators) the successor. */
export function useBoardOwnership(boardId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.collab.ownership(boardId),
    queryFn: ({ signal }) => api.get<BoardOwnership>(`${base(boardId)}/ownership`, { signal }),
    enabled,
  });
}

/**
 * Hand the board over (`userId: null` = the automatic succession). `revokeAccess` ALSO denies the
 * previous owner: a different operation from a plain hand-over.
 */
export function useChangeBoardOwner(boardId: string) {
  const invalidate = useInvalidateBoardAccess(boardId);
  return useMutation({
    mutationFn: ({ userId, revokeAccess }: { userId: string | null; revokeAccess: boolean }) =>
      api.put<BoardOwnership>(`${base(boardId)}/owner`, {
        user_id: userId,
        revoke_access: revokeAccess,
      }),
    onSuccess: invalidate,
  });
}

export function useSetOriginTeam(boardId: string) {
  const invalidate = useInvalidateBoardAccess(boardId);
  return useMutation({
    mutationFn: (teamId: string | null) =>
      api.put<BoardOwnership>(`${base(boardId)}/origin-team`, { team_id: teamId }),
    onSuccess: invalidate,
  });
}

// --- board labels of one board ---------------------------------------------------------------

export function useBoardLabelIds(boardId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.collab.boardLabelIds(boardId),
    queryFn: ({ signal }) => api.get<string[]>(`${base(boardId)}/board-labels`, { signal }),
    enabled,
  });
}

/** The labels in the order the chips are shown (a pure reorder changes no access). */
export function useSetBoardLabels(boardId: string) {
  const invalidate = useInvalidateBoardAccess(boardId);
  return useMutation({
    mutationFn: ({ labelIds, dryRun = false }: { labelIds: string[]; dryRun?: boolean }) =>
      api.put<{ label_ids: string[]; impact: Impact }>(`${base(boardId)}/board-labels`, {
        label_ids: labelIds,
        dry_run: dryRun,
      }),
    onSuccess: (_result, { dryRun }) => {
      if (!dryRun) invalidate();
    },
  });
}

// --- invitations (direct) ---------------------------------------------------------------------

type Scope =
  | { kind: 'workspace'; id: string }
  | { kind: 'board'; id: string }
  | { kind: 'database'; id: string };
const scopePath = (scope: Scope) =>
  scope.kind === 'workspace'
    ? `/v1/workspaces/${scope.id}`
    : scope.kind === 'board'
      ? `/v1/boards/${scope.id}`
      : `/v1/databases/${scope.id}`;

function useInvalidateInvitations() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ['collab'] });
  };
}

export function useScopeInvitations(scope: Scope, enabled = true) {
  const { kind, id } = scope;
  return useQuery({
    queryKey: queryKeys.collab.invitations(kind, id),
    queryFn: ({ signal }) =>
      api.get<Invitation[]>(`${scopePath({ kind, id })}/invitations`, { signal }),
    enabled,
  });
}

export function useInvite(scope: Scope) {
  const invalidate = useInvalidateInvitations();
  return useMutation({
    mutationFn: (data: { identifier: string } & RoleInput) =>
      api.post<Invitation>(`${scopePath(scope)}/invitations`, data),
    onSuccess: invalidate,
  });
}

/** Renew a pending database invitation (a new term, and the invitee is told again). */
export function useResendInvitation() {
  const invalidate = useInvalidateInvitations();
  return useMutation({
    mutationFn: (invitationId: string) =>
      api.post<Invitation>(`/v1/invitations/${invitationId}/resend`),
    onSuccess: invalidate,
  });
}

export function useRevokeInvitation() {
  const invalidate = useInvalidateInvitations();
  return useMutation({
    mutationFn: (invitationId: string) => api.delete(`/v1/invitations/${invitationId}`),
    onSuccess: invalidate,
  });
}

export function useMyInvitations() {
  return useQuery({
    queryKey: queryKeys.collab.myInvitations,
    queryFn: ({ signal }) => api.get<Invitation[]>('/v1/me/invitations', { signal }),
  });
}

export function useAnswerInvitation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      invitationId,
      accept,
    }: {
      invitationId: string;
      accept: boolean;
    }): Promise<RedeemResult | null> => {
      if (accept) return api.post<RedeemResult>(`/v1/invitations/${invitationId}/accept`);
      await api.post(`/v1/invitations/${invitationId}/decline`);
      return null;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['collab'] });
      void client.invalidateQueries({ queryKey: queryKeys.workspaces.all });
      void client.invalidateQueries({ queryKey: queryKeys.boards.all });
      // A database invitation became a direct role: lists, access and the catalog change.
      void client.invalidateQueries({ queryKey: queryKeys.databases.all });
    },
  });
}

// --- invitation links -------------------------------------------------------------------------

export type LinkInput = RoleInput & {
  /** The absolute expiry as an ISO instant (built from the date + time pickers). */
  expires_at: string;
  max_uses: number;
  requires_approval: boolean;
};

export function useScopeLinks(scope: Scope, enabled = true) {
  const { kind, id } = scope;
  return useQuery({
    queryKey: queryKeys.collab.links(kind, id),
    queryFn: ({ signal }) =>
      api.get<InviteLink[]>(`${scopePath({ kind, id })}/invite-links`, { signal }),
    enabled,
  });
}

export function useCreateLink(scope: Scope) {
  const invalidate = useInvalidateInvitations();
  return useMutation({
    mutationFn: (data: LinkInput) =>
      api.post<{ link: InviteLink; token: string }>(`${scopePath(scope)}/invite-links`, data),
    onSuccess: invalidate,
  });
}

export function useRevokeLink() {
  const invalidate = useInvalidateInvitations();
  return useMutation({
    mutationFn: (linkId: string) => api.delete(`/v1/invite-links/${linkId}`),
    onSuccess: invalidate,
  });
}

export function usePreviewLink() {
  return useMutation({
    mutationFn: (token: string) => api.post<LinkPreview>('/v1/invite-links/preview', { token }),
  });
}

export function useRedeemLink() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => api.post<RedeemResult>('/v1/invite-links/redeem', { token }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['collab'] });
      void client.invalidateQueries({ queryKey: queryKeys.workspaces.all });
      void client.invalidateQueries({ queryKey: queryKeys.boards.all });
    },
  });
}

// --- requests and approvals -------------------------------------------------------------------

export function useWorkspaceRequests(
  workspaceId: string,
  includeResolved: boolean,
  enabled = true,
) {
  return useQuery({
    queryKey: [...queryKeys.collab.requests(workspaceId), { includeResolved }],
    queryFn: ({ signal }) =>
      api.get<AccessRequest[]>(`/v1/workspaces/${workspaceId}/requests`, {
        signal,
        query: { include_resolved: includeResolved },
      }),
    enabled,
  });
}

export function useBoardRequests(boardId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.collab.boardRequests(boardId),
    queryFn: ({ signal }) => api.get<AccessRequest[]>(`${base(boardId)}/requests`, { signal }),
    enabled,
  });
}

export function useMyRequests() {
  return useQuery({
    queryKey: queryKeys.collab.myRequests,
    queryFn: ({ signal }) => api.get<AccessRequest[]>('/v1/me/requests', { signal }),
  });
}

export function useResolveRequest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      approve,
      newOwnerUserId,
    }: {
      requestId: string;
      approve: boolean;
      /** Destination side of a transfer: the person who will own the board. */
      newOwnerUserId?: string | null;
    }) =>
      api.post<AccessRequest>(
        `/v1/requests/${requestId}/${approve ? 'approve' : 'reject'}`,
        approve && newOwnerUserId ? { new_owner_user_id: newOwnerUserId } : undefined,
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['collab'] });
      void client.invalidateQueries({ queryKey: queryKeys.boards.all });
      void client.invalidateQueries({ queryKey: queryKeys.workspaces.all });
    },
  });
}

export function useCancelRequest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => api.delete(`/v1/requests/${requestId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['collab'] });
    },
  });
}

// --- transfers --------------------------------------------------------------------------------

export function useTransferImpact(boardId: string, destinationId: string | null) {
  return useQuery({
    queryKey: queryKeys.collab.transferImpact(boardId, destinationId ?? ''),
    queryFn: ({ signal }) =>
      api.get<TransferImpact>(`${base(boardId)}/transfer-impact`, {
        signal,
        query: { destination_workspace_id: destinationId },
      }),
    enabled: destinationId !== null,
  });
}

export function useStartTransfer(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      destination_workspace_id: string;
      confirm: boolean;
      confirm_name: string;
      new_owner_user_id?: string | null;
    }) => api.post<TransferResult>(`${base(boardId)}/transfer`, data),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['collab'] });
      void client.invalidateQueries({ queryKey: queryKeys.boards.all });
      void client.invalidateQueries({ queryKey: queryKeys.workspaces.all });
    },
  });
}

export function useReleaseTransferRestriction(boardId: string) {
  const invalidate = useInvalidateBoardAccess(boardId);
  return useMutation({
    mutationFn: () => api.delete(`${base(boardId)}/transfer-restriction`),
    onSuccess: invalidate,
  });
}

/** The teams and workspace roles given THIS board (and a way to give, change or take one away). */
export function useBoardRules(boardId: string, enabled = true) {
  return useQuery({
    queryKey: ['collab', 'board-rules', boardId],
    queryFn: ({ signal }) => api.get<AccessRule[]>(`${base(boardId)}/access-rules`, { signal }),
    enabled,
  });
}

export interface BoardRuleInput {
  subject_kind: 'team' | 'role' | 'custom_role';
  team_id?: string;
  subject_role?: string;
  custom_role_id?: string;
  granted_role: MemberRole;
}

export function useSetBoardRule(boardId: string) {
  const invalidate = useInvalidateBoardAccess(boardId);
  return useMutation({
    mutationFn: (rule: BoardRuleInput) =>
      api.put<{ rule: AccessRule | null; impact: Impact }>(`${base(boardId)}/access-rules`, rule),
    onSuccess: invalidate,
  });
}

export function useDeleteBoardRule(boardId: string) {
  const invalidate = useInvalidateBoardAccess(boardId);
  return useMutation({
    mutationFn: (ruleId: string) => api.delete(`${base(boardId)}/access-rules/${ruleId}`),
    onSuccess: invalidate,
  });
}

export interface KnownPerson {
  id: string;
  display_name: string;
  /** Unique: tells apart people with the same display name. Never an e-mail address. */
  login: string;
}

/** Colleagues (people sharing a workspace with the viewer) by display name or login. */
export function usePeopleSearch(text: string) {
  const query = text.trim();
  return useQuery({
    // An e-mail address is typed exactly; names and logins are searched.
    enabled: query.length >= 2 && !query.includes('@'),
    queryKey: ['people', query] as const,
    queryFn: ({ signal }) =>
      api.get<KnownPerson[]>('/v1/account/people', { signal, query: { q: query } }),
    staleTime: 30_000,
  });
}
